import type { PostSource, StatusValue } from "../types";

export type { PostSource };

export type EffectiveStatusValue = Exclude<StatusValue, "stale">;

export const EFFECTIVE_STATUS_VALUES: readonly EffectiveStatusValue[] = [
  "none",
  "possible",
  "scheduled",
  "ongoing",
  "restored",
  "cancelled",
  "unknown",
] as const;

export function isEffectiveStatusValue(value: unknown): value is EffectiveStatusValue {
  return typeof value === "string" && EFFECTIVE_STATUS_VALUES.includes(value as EffectiveStatusValue);
}

export interface ApiWindow {
  start: string;
  end: string;
  localLabel?: string;
}

export interface HealthResponse {
  generatedAt: string;
  ok: boolean;
  stale: boolean;
  database: { ok: boolean };
  poller: {
    lastSuccessAt: string | null;
    lastEditScanAt: string | null;
    consecutiveFailures: number;
    latestStrategy: string | null;
    latestPostCount: number;
    warnings: string[];
  };
  strategies: {
    name: string;
    lastAttemptAt: string | null;
    lastSuccessAt: string | null;
    consecutiveFailures: number;
  }[];
}

export type TracerConfidence = "high" | "medium" | "low";

export interface StatusAreaSummary {
  key: string;
  mapped: boolean;
  status: EffectiveStatusValue;
  stale: boolean;
  lastKnownStatus: EffectiveStatusValue;
  label: string;
  activeWindow: ApiWindow | null;
  nextWindow: ApiWindow | null;
  sourcePostId: string | null;
  lastUpdateAt: string | null;
  source: PostSource | null;
  /** R-672: absent on older servers means not provisional. */
  provisional?: boolean;
  mapUrl?: string | null;
}

export interface StatusResponse {
  generatedAt: string;
  stale: boolean;
  lastSuccessfulFetchAt: string | null;
  counts: Record<EffectiveStatusValue, number>;
  areas: StatusAreaSummary[];
}

export type AreaPosition = [number, number];
export type LinearRing = AreaPosition[];
export type PolygonCoordinates = LinearRing[];
export type MultiPolygonCoordinates = PolygonCoordinates[];

export interface PolygonGeometry {
  type: "Polygon";
  coordinates: PolygonCoordinates;
}

export interface MultiPolygonGeometry {
  type: "MultiPolygon";
  coordinates: MultiPolygonCoordinates;
}

export type AreaGeometry = PolygonGeometry | MultiPolygonGeometry;

export interface AreaFeatureProperties {
  key: string;
  status: EffectiveStatusValue;
  stale: boolean;
  lastKnownStatus: EffectiveStatusValue;
  label: string;
  names: string[];
  activeWindow: ApiWindow | null;
  nextWindow: ApiWindow | null;
  bbox?: [number, number, number, number];
  centroid?: [number, number];
  polygon_count?: number;
  /** R-672: absent on older servers means not provisional. */
  provisional?: boolean;
  mapUrl?: string | null;
  tracerConfidence?: TracerConfidence | null;
}

export interface AreaFeature {
  type: "Feature";
  id?: string | number;
  properties: AreaFeatureProperties;
  geometry: AreaGeometry;
}

export interface AreaFeatureCollection {
  type: "FeatureCollection";
  name?: string;
  features: AreaFeature[];
}

export interface AreaStatusDetail {
  value: EffectiveStatusValue;
  stale: boolean;
  label: string;
  lastKnownStatus: EffectiveStatusValue;
  activeWindow: ApiWindow | null;
  nextWindow: ApiWindow | null;
  sourcePostId: string | null;
  sourceUrl: string | null;
  source: PostSource | null;
}

export interface AreaDetail {
  key: string;
  mapped: boolean;
  displayName: string;
  names: string[];
  status: AreaStatusDetail;
  /** R-672: absent on older servers means not provisional. */
  provisional?: boolean;
  mapUrl?: string | null;
}

export interface AreaDetailResponse {
  generatedAt: string;
  area: AreaDetail;
}

export interface AreaFeedItem {
  eventId: string;
  kind: string;
  statusEffect: EffectiveStatusValue;
  text: string;
  window: ApiWindow | null;
  postId: string;
  postUrl: string | null;
  postCreatedAt: string | null;
  postMissing: boolean;
  source: PostSource;
  /** R-630/R-659: true when a newer schedule, an edit, a missing post, or a newer source replaced this event. */
  superseded?: boolean;
  supersededReason?:
    | "master_revision"
    | "edited_post"
    | "seeded_master_replaced"
    | "post_missing"
    | "newer_source"
    | "sheet_revision"
    | null;
}

export interface AreaFeedResponse {
  generatedAt: string;
  areaKey: string;
  items: AreaFeedItem[];
}

export interface UnmatchedItem {
  eventId: string;
  kind: string;
  statusEffect: EffectiveStatusValue;
  areaText: string;
  rawText: string;
  window: ApiWindow | null;
  postId: string;
  postUrl: string | null;
  parserWarnings: string[];
}

export interface UnmatchedResponse {
  generatedAt: string;
  items: UnmatchedItem[];
}

export type AreaReportState = "on" | "off";

export interface AreaReportRequest {
  state: AreaReportState;
  clientToken: string;
  observedTier: "off" | "soon" | "later" | "clear" | "unknown" | null;
  observedStatus: string | null;
  windowStart: string | null;
  windowEnd: string | null;
}

export interface AreaReportAcceptedResponse {
  generatedAt: string;
  areaKey: string;
  accepted: true;
  replaced: boolean;
}

export interface AreaReportSummaryResponse {
  generatedAt: string;
  areaKey: string;
  windowMinutes: number;
  minimumReports: number;
  visible: boolean;
  reports: { on: number; off: number; total: number } | null;
}
